import { useEffect, useState } from 'react';
import PageContentWrapper from 'src/components/PageContentWrapper/PageContentWrapper';
import { CreateProjectStepsTitle } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStepsTitle/CreateProjectStepsTitle';
import { Button } from 'src/components/ui/button';
import {
  useGetKnowledgeBaseQuery,
  useLazyGetCollectorsStatusQuery,
  useRunKnowledgeBaseCollectorMutation,
} from 'src/services';
import { useLoader } from 'src/provider/LoaderProvider';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'src/store';
import { setCreationFlow } from 'src/store/slices/creationFlowSlices';
import { CreateProjectHead } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectHead/CreateProjectHead';
import { UseExistingKBAccordion } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStep2Page/UseExistingKBAccordion/UseExistingKBAccordion';
import { CreateNewKBAccordion } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStep2Page/CreateNewKBAccordion/CreateNewKBAccordion';
import { Separator } from 'src/components/ui/separator';
import { API_VERSION } from 'src/constants/api-routes';
import { getCreateProjectStepPath } from 'src/pages/ProjectPage/CreateProjectSteps/createProjectStepPath';
import type { KnowledgeBase, KnowledgeBaseCollector } from 'src/services/knowledgeBaseService';
import { getApiVersion } from 'src/utils/apiVersion';

const filterKnowledgeBases = (items: KnowledgeBase[], excludeId: string | number | null) => {
  return items.filter((item) => `${item.id}` !== `${excludeId}`);
};

const findNewKnowledgeBase = (items: KnowledgeBase[], kbId: string | number | null) => {
  return items.find((item) => `${item.id}` === `${kbId}`);
};

const CreateProjectStep2Page = () => {
  const loader = useLoader();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const navigate = useNavigate();
  const { id, step } = useParams();
  const { projectType } = useAppSelector((s) => s.projectsSlices);
  const creationFlowState = useAppSelector((s) => s.creationFlowSlices);
  const dispatch = useAppDispatch();
  const projectTypeVar = projectType || type;
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [newKnowledgeBase, setNewKnowledgeBase] = useState<KnowledgeBase | null>(null);
  const [isContinueDisabled, setIsContinueDisabled] = useState(true);
  const [collectors, setCollectors] = useState<KnowledgeBaseCollector[]>([]);
  const { data: knowledgeBaseData } = useGetKnowledgeBaseQuery(
    {
      load_projects: true,
      page_size: 100,
    },
    { refetchOnMountOrArgChange: true, skip: !id },
  );
  const [fetchCollectorsStatus] = useLazyGetCollectorsStatusQuery();
  const [runKnowledgeBaseCollector] = useRunKnowledgeBaseCollectorMutation();

  useEffect(() => {
    const config = creationFlowState[id];

    if (!config || (config && +config.step < +step)) {
      dispatch(
        setCreationFlow({
          id: id,
          step: step,
          formData: null,
        }),
      );
    }
  }, []);

  useEffect(() => {
    if (knowledgeBaseData) {
      const config = creationFlowState[id];
      const excludeKbId = config?.knowledgeBaseId;

      if (excludeKbId) {
        setKnowledgeBases(filterKnowledgeBases(knowledgeBaseData.items, excludeKbId));
        setNewKnowledgeBase(findNewKnowledgeBase(knowledgeBaseData.items, excludeKbId));
      } else {
        setKnowledgeBases(knowledgeBaseData.items);
        setNewKnowledgeBase(null);
      }
      loader.hide();
    }
  }, [knowledgeBaseData, creationFlowState]);

  useEffect(() => {
    if (newKnowledgeBase) {
      setIsContinueDisabled(false);
      return;
    }

    if (knowledgeBases?.length) {
      const isContinueEnabled = knowledgeBases.some((item) => {
        return item.projects.some((project) => `${project.id}` === `${id}`);
      });

      setIsContinueDisabled(!isContinueEnabled);
    }
  }, [knowledgeBases, newKnowledgeBase]);

  // V1 reruns idle or failed collectors; V2 reruns every collector of a syncable type.
  const shouldRunCollector = async (collector: KnowledgeBaseCollector, version: string) => {
    if (!collector) return false;

    if (version === API_VERSION.V1) {
      const { data } = await fetchCollectorsStatus({ collectorId: collector.id });
      return ['idle', 'failed'].includes(data?.status);
    }

    return (
      version === API_VERSION.V2 &&
      ['zendesk_tickets', 'zendesk_helpcenter', 'freshdesk_tickets', 'web_scrapper'].includes(
        collector.type,
      )
    );
  };

  const next = async () => {
    const promises = [];
    setIsContinueDisabled(true);
    const version = getApiVersion();

    for (const collector of collectors) {
      if (await shouldRunCollector(collector, version)) {
        promises.push(
          runKnowledgeBaseCollector({
            knowledgeBaseId: newKnowledgeBase.id,
            collectorId: collector.id,
          }),
        );
      }
    }

    await Promise.all(promises);
    setIsContinueDisabled(false);

    if (version === API_VERSION.V2) {
      dispatch(
        setCreationFlow({
          id: id,
          promptType: 'system',
        }),
      );
    }

    navigate(
      getCreateProjectStepPath(
        id,
        version === API_VERSION.V2 ? +step + 2 : +step + 1,
        projectTypeVar,
      ),
      { replace: true },
    );
  };

  const onCollectorsChanged = (collectors: KnowledgeBaseCollector[]) => {
    setCollectors(collectors);
  };

  const back = () => {
    navigate(getCreateProjectStepPath(id, +step - 1, projectTypeVar), { replace: true });
  };

  return (
    <PageContentWrapper
      menuBarVisible={false}
      className={'create-project-step items-center !gap-5'}
    >
      <CreateProjectStepsTitle step={+step} title={'AI Name'} />
      <CreateProjectHead step={+step} titleIndex={1} />
      {Boolean(knowledgeBases.length) && (
        <div className={'flex flex-col w-[455px]'}>
          <UseExistingKBAccordion items={knowledgeBases} />
          <Separator className={'bg-[#E3E6E8] mt-5'} />
        </div>
      )}
      <div className={'flex flex-col w-full items-center'}>
        <CreateNewKBAccordion item={newKnowledgeBase} onCollectorsChanged={onCollectorsChanged} />
      </div>
      <div className={'w-[455px] flex flex-col gap-5 items-center'}>
        <Button
          disabled={isContinueDisabled}
          onClick={next}
          className="min-w-24 w-full flex flex-row h-12"
        >
          Continue
        </Button>
        <button
          type="button"
          onClick={back}
          className={'text-[#3F60B3] text-xs font-medium cursor-pointer hover:underline'}
        >
          ← Back
        </button>
      </div>
    </PageContentWrapper>
  );
};

export default CreateProjectStep2Page;
